<template>
  <PDFTron
    v-if="docs.length > 0"
    :initial-docs="docs"
    :path="pdftronPath"
    :owner-id="documentId"
    owner-type="Document"
    :enable-annotations="enableAnnotations"
    :enable-annotations-toolbar="enableAnnotationsToolbar"
  />
  <DocumentPreviewUnreadable v-else-if="unreadable" />
  <div
    v-else-if="error || !loading"
    class="tw-flex tw-items-center tw-justify-center tw-p-6"
  >
    <SfBannerInline
      variant="danger"
      :title="I18n.t('documents.preview.load_error')"
      class="tw-max-w-md"
    />
  </div>
</template>

<script setup lang="ts">
import PDFTron from './pdftron.vue'
import SfBannerInline from '@app/shared/vue/components/sf-banner/sf-banner-inline.vue'
import DocumentPreviewUnreadable from '@app/shared/document-preview/document-preview-unreadable.vue'
import useI18n from '@app/shared/vue/composables/use-I18n'
import { useDocumentPreview } from '@app/shared/document-preview/use-document-preview'

interface Props {
  documentId: number
  documentUrl?: string
  initialPdftronPath?: string
  enableAnnotations?: boolean
  enableAnnotationsToolbar?: boolean
}

const {
  documentId,
  documentUrl = undefined,
  initialPdftronPath = undefined,
  enableAnnotations = false,
  enableAnnotationsToolbar = false,
} = defineProps<Props>()

const I18n = useI18n()

// With a server-baked `documentUrl` PDFTron mounts straight away; without one
// (an email) the composable lazy-fetches `#preview`. An email the server cannot
// read gets its own notice. The banner covers both a failed fetch (`error`) and
// a fetch that settles without a usable URL (e.g. a 200 with `url: null` while
// the PDF rendition is still being generated); only an in-flight fetch renders
// nothing.
const { docs, pdftronPath, loading, error, unreadable } = useDocumentPreview(
  documentId,
  {
    url: documentUrl,
    pdftronPath: initialPdftronPath,
  }
)
</script>
